import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["enableButton"];

  declare readonly enableButtonTarget: HTMLElement;
  declare readonly hasEnableButtonTarget: boolean;

  private swRegistration: ServiceWorkerRegistration | null = null;

  connect() {
    this.setupPushNotifications().catch((error) => {
      console.error("Failed to setup push notifications", error);
    });
  }

  enableButtonTargetConnected() {
    this.updateEnableButtonVisibility();
  }

  private updateEnableButtonVisibility() {
    if (!this.hasEnableButtonTarget) {
      return;
    }

    // Show the button only if permission is "default" (not accepted nor rejected)
    // and if Push API is supported
    const shouldShow =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window &&
      Notification.permission === "default";

    this.enableButtonTarget.hidden = !shouldShow;
  }

  async enableNotifications(event: Event) {
    event.preventDefault();

    // iOS requires the permission prompt to be opened directly from the
    // user gesture. Do this before any awaited work, such as fetching the
    // push config or waiting for service-worker registration.
    if (!(await this.requestNotificationPermission())) {
      this.updateEnableButtonVisibility();
      return;
    }

    await this.enablePushNotifications();
    // Update button visibility after attempting to enable
    this.updateEnableButtonVisibility();
  }

  private async setupPushNotifications() {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const version = window.BudgeterConfig.sw_cache_key;
    this.swRegistration = await navigator.serviceWorker.register(
      `/sw.js?v=${version}`,
      { updateViaCache: "none" },
    );

    // Only auto-enable if already granted
    // On iOS, permission will be "default" and won't work without user interaction
    if ("Notification" in window && Notification.permission === "granted") {
      await this.enablePushNotifications();
    }
  }

  async enablePushNotifications() {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    if (!this.swRegistration) {
      this.swRegistration = await navigator.serviceWorker.ready;
    }

    const swRegistration = this.swRegistration;
    if (!swRegistration) {
      return;
    }

    // Check if Push API is supported (not available on iOS Safari)
    if (!("PushManager" in window) || !("pushManager" in swRegistration)) {
      console.log("Push notifications not supported on this browser");
      return;
    }

    if (!(await this.requestNotificationPermission())) {
      return;
    }

    const pushConfig = await this.getPushConfig();
    if (!pushConfig.enabled || !pushConfig.public_key) {
      return;
    }

    const existingSubscription =
      await swRegistration.pushManager.getSubscription();
    if (existingSubscription) {
      await this.persistSubscription(existingSubscription);
      return;
    }

    try {
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.base64ToArrayBuffer(pushConfig.public_key),
      });

      await this.persistSubscription(subscription);
    } catch (error) {
      // iOS and some browsers don't support push subscriptions
      console.log("Failed to subscribe to push notifications:", error);
    }
  }

  private async requestNotificationPermission(): Promise<boolean> {
    if (!("Notification" in window)) {
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  private async getPushConfig(): Promise<{
    enabled: boolean;
    public_key: string;
  }> {
    const response = await fetch("/pwa/push/config", {
      credentials: "same-origin",
    });

    if (!response.ok) {
      return { enabled: false, public_key: "" };
    }

    return response.json();
  }

  private async persistSubscription(subscription: PushSubscription) {
    const subscriptionData = subscription.toJSON();

    // Check if this subscription already exists in the backend
    const alreadyExists = await this.subscriptionExists(
      subscriptionData.endpoint!,
    );

    if (alreadyExists) {
      return;
    }

    // Only persist if it doesn't exist
    const csrfToken = await this.getCsrfToken();

    await fetch("/notifications/push/subscription", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(subscriptionData),
    });
  }

  private async subscriptionExists(endpoint: string): Promise<boolean> {
    try {
      const response = await fetch(
        `/notifications/push/subscription?endpoint=${encodeURIComponent(endpoint)}`,
        {
          method: "GET",
          credentials: "same-origin",
        },
      );

      if (!response.ok) {
        console.warn("Failed to check subscription existence:", response.status);
        return false;
      }

      const result = (await response.json()) as { exists: boolean };
      return result.exists;
    } catch (error) {
      console.warn("Error checking subscription existence:", error);
      return false;
    }
  }

  private async getCsrfToken(): Promise<string> {
    const response = await fetch("/pwa/csrf-token", {
      credentials: "same-origin",
    });

    if (!response.ok) {
      return "";
    }

    const payload = (await response.json()) as { token: string };
    return payload.token;
  }

  private base64ToArrayBuffer(base64String: string): ArrayBuffer {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputBuffer = new ArrayBuffer(rawData.length);
    const outputArray = new Uint8Array(outputBuffer);

    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputBuffer;
  }
}
